/**
 * Pure date/time helpers behind editing an event -- `Event.tsx`'s own edit
 * form.
 *
 * `dateParam`/`timeParam` read the same as `FindTime.tsx`'s own local copies
 * of the same two functions, deliberately not imported from there: that file
 * is not exported from, a parallel task is mid-edit on it, and the
 * campaign's own task split says shared bits belong in a new file rather
 * than a read of `FindTime.tsx`.
 */

export function dateParam(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function timeParam(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** A `Field`'s `date`/`time` pair, put back together as a plain, browser-zone
 *  `Date` -- what "Propose a new time" (agora#286) sends a `Date` object to
 *  `counter()`/`eventTimeLabel` for, and what a floating-time (`timeZone:
 *  null`) event's own edit reads the browser's zone through as well. `null`
 *  for a pair that does not parse, so a half-filled form disables Send
 *  rather than proposing a bad date. */
export function combineDateTime(dateStr: string, timeStr: string): Date | null {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [h, min] = timeStr.split(':').map(Number);
  if (
    !y ||
    !m ||
    !d ||
    h === undefined ||
    Number.isNaN(h) ||
    min === undefined ||
    Number.isNaN(min)
  ) {
    return null;
  }
  return new Date(y, m - 1, d, h, min);
}

/** A bare `date`, at local midnight -- an all-day field's own day, with no
 *  time part to combine. `null` for one that does not parse. */
export function parseDateOnly(dateStr: string): Date | null {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** The whole-day `P<n>D` duration spanning `startDay` to `endDay`,
 *  inclusive of both -- the same day picked twice is one day, `P1D`, never
 *  `PT24H`: an all-day event's duration is a day count, not a time span
 *  (this ticket's own rule). `endDay` before `startDay` still floors at one
 *  day rather than a negative span the caller should have refused earlier. */
export function wholeDayDuration(startDay: Date, endDay: Date): string {
  const days = Math.max(1, Math.round((endDay.getTime() - startDay.getTime()) / DAY_MS) + 1);
  return `P${days}D`;
}

/** The inverse: how many whole days a `P<n>D` duration spans. Only the shape
 *  an all-day event's own `duration` actually uses -- `layout.ts`'s
 *  `durationMinutes` already owns the `PT..` shape a timed event uses, and
 *  reads a `P<n>D` one as zero minutes; this is the other half, not a
 *  replacement for it. Unparseable floors at one day, never zero -- a
 *  one-day event is the floor, not "could not tell". */
export function wholeDays(duration: string): number {
  const match = /^P(\d+)D$/.exec(duration);
  return match ? Math.max(1, Number(match[1])) : 1;
}

const ALLDAY_DATE = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/** "Thursday 24 September", or "24 September – 26 September" across more
 *  than one day -- an all-day event's own label, standing in for
 *  `eventTimeLabel`'s time range, which a whole day has none of. */
export function allDayLabel(start: Date, days: number): string {
  if (days <= 1) return ALLDAY_DATE.format(start);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + days - 1);
  return `${ALLDAY_DATE.format(start)} – ${ALLDAY_DATE.format(end)}`;
}

/** Parse a `Field`'s `date`/`time` pair into its parts. `null` for a pair
 *  that does not parse, so a half-filled form can refuse to compute
 *  anything from it rather than silently reading `NaN` fields as zero. */
function parseDateTimeParts(
  dateStr: string,
  timeStr: string,
): { y: number; m: number; d: number; h: number; min: number } | null {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [h, min] = timeStr.split(':').map(Number);
  if (
    !y ||
    !m ||
    !d ||
    h === undefined ||
    Number.isNaN(h) ||
    min === undefined ||
    Number.isNaN(min)
  ) {
    return null;
  }
  return { y, m, d, h, min };
}

/** The zone's own UTC offset, in minutes, at `utcMs` -- read by formatting
 *  that instant in `timeZone` and comparing the wall clock it prints
 *  against the same instant read as if it had been UTC all along. Positive
 *  east of UTC, matching `Date.getTimezoneOffset`'s own sign flipped
 *  (`Intl`, not that method, because the zone in question is the event's
 *  own, never the browser's). */
function offsetMinutesAt(utcMs: number, timeZone: string): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(utcMs))
      .map((p) => [p.type, p.value]),
  );
  const asIfUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return (asIfUtc - utcMs) / 60_000;
}

/**
 * The UTC instant a wall clock of `y/m/d/h/min` reads as, in `timeZone` --
 * correct across a daylight-saving change, unlike building a `Date` straight
 * from those fields (which reads them in the *browser's* own zone, wrong
 * the moment the event's zone differs from it and a change sits between two
 * times being compared: a duration spanning a fall-back is really an hour
 * longer than the same wall-clock hours would be outside it).
 *
 * Two passes: the first guesses the zone's offset from the wall clock read
 * as if it were already UTC, the second re-reads the offset at that
 * corrected instant. One pass settles everywhere except the instant right
 * at a transition; two settle there too, since the offset changes at most
 * once between two nearby guesses.
 */
function zonedEpochInZone(
  y: number,
  m: number,
  d: number,
  h: number,
  min: number,
  timeZone: string,
): number {
  const naive = Date.UTC(y, m - 1, d, h, min);
  let guess = naive;
  for (let i = 0; i < 2; i++) {
    guess = naive - offsetMinutesAt(guess, timeZone) * 60_000;
  }
  return guess;
}

/**
 * A `Field`'s `date`/`time` pair, as the UTC instant it names -- in
 * `timeZone` when the event has one, or in the browser's own zone (via a
 * plain `Date`) when it does not: a `timeZone: null` event is JSCalendar's
 * floating time, which has no zone of its own and has always meant
 * "whichever the viewer is in" here. `null` for a pair that does not parse,
 * so a half-filled form disables Save rather than sending a bad date.
 */
export function zonedEpoch(
  dateStr: string,
  timeStr: string,
  timeZone: string | null,
): number | null {
  const parts = parseDateTimeParts(dateStr, timeStr);
  if (!parts) return null;
  const { y, m, d, h, min } = parts;
  if (timeZone === null) return new Date(y, m - 1, d, h, min).getTime();
  return zonedEpochInZone(y, m, d, h, min, timeZone);
}

/** The inverse of `zonedEpoch`: the `date`/`time` pair a `Field` should show
 *  for `epochMs`, read in `timeZone` (or the browser's own zone, for a
 *  floating time). */
export function zonedDateParam(
  epochMs: number,
  timeZone: string | null,
): { date: string; time: string } {
  if (timeZone === null) {
    const d = new Date(epochMs);
    return { date: dateParam(d), time: timeParam(d) };
  }
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
      .formatToParts(new Date(epochMs))
      .map((p) => [p.type, p.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}

/** `CalendarEvent.start`'s own shape (`YYYY-MM-DDTHH:MM:SS`, no offset)
 *  split back into the `date`/`time` pair a `Field` takes -- the plain
 *  string surgery this needs, since the value is already local to whatever
 *  zone the event names and wants no further conversion to be read apart. */
export function splitLocalIso(iso: string): { date: string; time: string } {
  const [date = '', timePart = ''] = iso.split('T');
  return { date, time: timePart.slice(0, 5) };
}
